import { useSearchParams, Link } from 'react-router-dom'
import { format } from 'date-fns'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { KnowledgeFrame } from '../components/KnowledgeNav'
import { RuleStatus } from '../components/ConceptCuration'
import { describeRule } from '../components/ruleText'
import { useRunChanges, useRuns } from '../hooks/useKnowledge'
import type { RunChange } from '../api/knowledgeApi'

const n = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString())

function ChangeList({ title, items, empty }: { title: string; items: RunChange[]; empty: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{title} ({items.length})</CardTitle>
      </CardHeader>
      <CardContent className="max-h-96 space-y-1 overflow-auto text-sm">
        {items.length === 0 && <p className="text-slate-500">{empty}</p>}
        {items.map((c) => (
          <div key={c.ucid} className="flex justify-between gap-2">
            <Link to={`/app/knowledge/${c.detail.into ?? c.ucid}`} className="truncate hover:underline">
              {c.name}
              {c.detail.intoName && <span className="text-slate-500"> → {c.detail.intoName}</span>}
            </Link>
            {c.detail.artworkCount != null && <span className="shrink-0 text-xs tabular-nums text-slate-400">{c.detail.artworkCount}</span>}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

export function ChangesPage() {
  const [params, setParams] = useSearchParams()
  const { data: runs, isLoading } = useRuns()
  const runId = params.get('run') || runs?.data[0]?.runId
  const { data: changes, isFetching } = useRunChanges(runId)

  return (
    <KnowledgeFrame
      title="Changes"
      description="What each nightly build changed: concepts that appeared, retired or were merged into another, and what the build did with each curation rule."
    >
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Build</TableHead>
                <TableHead className="text-right">Concepts</TableHead>
                <TableHead className="text-right">New</TableHead>
                <TableHead className="text-right">Retired</TableHead>
                <TableHead className="text-right">Merged</TableHead>
                <TableHead className="text-right">Rules applied</TableHead>
                <TableHead className="text-right">Artwork links</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={7}><Skeleton className="h-5 w-full" /></TableCell></TableRow>
              )}
              {runs?.data.map((r) => (
                <TableRow
                  key={r.runId}
                  className={`cursor-pointer ${r.runId === runId ? 'bg-slate-50' : ''}`}
                  onClick={() => setParams({ run: r.runId })}
                >
                  <TableCell>
                    <div className="font-medium">{format(new Date(r.syncedAt), 'MMM d, HH:mm')}</div>
                    <div className="font-mono text-[11px] text-slate-400">{r.runId}</div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{n(r.concepts)}</TableCell>
                  <TableCell className="text-right tabular-nums">{n(r.newConcepts)}</TableCell>
                  <TableCell className="text-right tabular-nums">{n(r.retiredConcepts)}</TableCell>
                  <TableCell className="text-right tabular-nums">{n(r.mergedConcepts)}</TableCell>
                  <TableCell className="text-right tabular-nums">{n(r.rulesApplied)}</TableCell>
                  <TableCell className="text-right tabular-nums">{n(r.artworkLinks)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {isFetching && <Skeleton className="h-48 w-full" />}
      {changes && !isFetching && (
        <>
          {changes.run.newConcepts == null && (
            <p className="text-sm text-slate-500">
              This was the first build synced, so there is nothing to compare it with.
            </p>
          )}
          <div className="grid gap-4 lg:grid-cols-3">
            <ChangeList title="New" items={changes.new} empty="No new concepts." />
            <ChangeList title="Merged away" items={changes.merged} empty="No concepts merged." />
            <ChangeList title="Retired" items={changes.retired} empty="No concepts retired." />
          </div>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Curation rules this build applied ({changes.applied.length})</CardTitle>
              <CardDescription>In the build&apos;s own words.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {changes.applied.length === 0 && <p className="text-slate-500">None.</p>}
              {changes.applied.map((r) => (
                <div key={r.id} className="flex flex-wrap justify-between gap-2 border-b pb-2 last:border-0">
                  <span>{describeRule(r)}</span>
                  <RuleStatus rule={r} />
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </KnowledgeFrame>
  )
}
